import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Card, Text, Loading } from "@nextui-org/react";
import { motion } from "framer-motion";
import { database } from "../firebase";

import AltLedgerHeader from "../components/alt-ledger/AltLedgerHeader";
import AltLedgerFilterCard from "../components/alt-ledger/AltLedgerFilterCard";
import AltLedgerTable from "../components/alt-ledger/AltLedgerTable";
import AltLedgerFooter from "../components/alt-ledger/AltLedgerFooter";
import { downloadAltLedgerExcel } from "../utils/altEntriesExport";

const AltEntriesLedger = () => {
  const [entries, setEntries] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [row, setRow] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: "created", asc: false });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);
      const [entriesSnap, deductionsSnap] = await Promise.all([
        database.collection("alt_entries").orderBy("created", "desc").get(),
        database
          .collection("alt_deductions")
          .orderBy("createdAt", "desc")
          .get(),
      ]);

      const entryDocs = entriesSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "entry",
        ...d.data(),
      }));

      const deductionDocs = deductionsSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "deduction",
        ...d.data(),
      }));

      setEntries(entryDocs);
      setDeductions(deductionDocs);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      asc: prev.key === key ? !prev.asc : true,
    }));
  };

  const processedList = useMemo(() => {
    let list = [...entries, ...deductions];

    // Filter
    if (search.trim() !== "") {
      const regex = new RegExp(search.trim(), "i");
      list = list.filter((doc) => {
        const isDeduction =
          doc.type === "deduction" || doc.recordType === "deduction";
        return (
          regex.test(isDeduction ? "Deduction" : "Entry") ||
          regex.test(doc.phone || "") ||
          regex.test(doc.customer || "") ||
          regex.test(doc.customerPhone || "") ||
          regex.test(doc.issue || "") ||
          regex.test(doc.note || "") ||
          regex.test(doc.clearBatchId || "") ||
          regex.test(doc.created || doc.createdAt || "")
        );
      });
    }

    // Sort
    const { key, asc } = sortConfig;
    list.sort((a, b) => {
      let valA, valB;
      const isDeductionA =
        a.type === "deduction" || a.recordType === "deduction";
      const isDeductionB =
        b.type === "deduction" || b.recordType === "deduction";

      if (key === "amount") {
        valA = isDeductionA ? -Number(a.amount || 0) : Number(a.amount || 0) * Number(a.qty || 1);
        valB = isDeductionB ? -Number(b.amount || 0) : Number(b.amount || 0) * Number(b.qty || 1);
      } else if (key === "customerAmount") {
        valA = isDeductionA ? 0 : Number(a.customerAmount || 0) * Number(a.qty || 1);
        valB = isDeductionB ? 0 : Number(b.customerAmount || 0) * Number(b.qty || 1);
      } else if (key === "profit") {
        valA = isDeductionA || a.category !== "customer" ? 0 : (Number(a.customerAmount || 0) - Number(a.amount || 0)) * Number(a.qty || 1);
        valB = isDeductionB || b.category !== "customer" ? 0 : (Number(b.customerAmount || 0) - Number(b.amount || 0)) * Number(b.qty || 1);
      } else if (key === "category") {
        valA = (a.category || "").toLowerCase();
        valB = (b.category || "").toLowerCase();
      } else if (key === "type") {
        valA = isDeductionA ? "Deduction" : "Entry";
        valB = isDeductionB ? "Deduction" : "Entry";
      } else if (key === "issue") {
        valA = (a.issue || a.note || "").toLowerCase();
        valB = (b.issue || b.note || "").toLowerCase();
      } else if (key === "created") {
        valA = new Date(a.created || a.createdAt || 0).getTime();
        valB = new Date(b.created || b.createdAt || 0).getTime();
      } else {
        valA = (a[key] || "").toString().toLowerCase();
        valB = (b[key] || "").toString().toLowerCase();
      }

      if (typeof valA === "string") {
        return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return asc ? valA - valB : valB - valA;
    });

    return list;
  }, [entries, deductions, search, sortConfig]);

  // Totals summary — 4-stat model
  const totals = useMemo(() => {
    let shopTotal = 0;
    let sunilC = 0;
    let customerTotal = 0;
    let deductionsTotal = 0;

    processedList.forEach((doc) => {
      const isDeduction =
        doc.type === "deduction" || doc.recordType === "deduction";
      if (isDeduction) {
        deductionsTotal += Number(doc.amount || 0);
      } else {
        const q = Number(doc.qty || 1);
        if (doc.category === "shop") {
          shopTotal += Number(doc.amount || 0) * q;
        } else if (doc.category === "customer") {
          sunilC += Number(doc.amount || 0) * q;
        }
        customerTotal += Number(doc.customerAmount || 0) * q;
      }
    });

    const netShopTotal = shopTotal - deductionsTotal;
    const profit = customerTotal - sunilC;

    return {
      shopTotal: netShopTotal,
      sunilC,
      customerTotal,
      profit,
      deductionsTotal,
    };
  }, [processedList]);

  return (
    <motion.div>
      {/* Header */}
      <AltLedgerHeader
        onExportAll={() => downloadAltLedgerExcel(processedList, "All")}
        onExportEntries={() => downloadAltLedgerExcel(processedList, "Entries")}
        onExportDeductions={() =>
          downloadAltLedgerExcel(processedList, "Deductions")
        }
      />

      {/* Table Container */}
      <motion.div className="tableDiv alignDivCenter">
        {loading ? (
          <Loading type="spinner" />
        ) : (
          <Fragment>
            {error && (
              <Text
                color="error"
                css={{
                  br: "12px",
                  bgColor: "$red50",
                  py: "5px",
                  px: "10px",
                  my: "10px",
                }}
              >
                An error occurred while trying to retrieve data from the server!
              </Text>
            )}

            <Card variant="bordered" className="cardShadow">
              <AltLedgerFilterCard
                row={row}
                onRowChange={setRow}
                search={search}
                onSearchChange={setSearch}
              />
              <Card.Divider />
              <Card.Body css={{ p: 0 }}>
                <AltLedgerTable
                  processedList={processedList}
                  row={row}
                  onSort={handleSort}
                />
              </Card.Body>
              <Card.Divider />
              <AltLedgerFooter totals={totals} />
            </Card>
          </Fragment>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AltEntriesLedger;

